import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, ChevronDown, ChevronLeft, ChevronRight, Check } from "lucide-react";
import api from "../../connector/api";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import TeacherStudentDetail from "./TeacherStudentDetail";

// Custom Dropdown Component
const CustomSelect = ({ value, onChange, options, placeholder, className = "", dropUp = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Ensure option matching converts both sides to strings:
  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-xs font-medium text-zinc-700 hover:bg-zinc-50 focus:outline-none focus:ring-1 focus:ring-zinc-400 transition-colors"
      >
        <span className="truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div
          className={`absolute z-50 w-full min-w-[140px] bg-white border border-zinc-200 rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto ${
            dropUp ? "bottom-full mb-1" : "mt-1"
          }`}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-left text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <span className="truncate">{option.label}</span>
              {String(value) === String(option.value) && (
                <Check className="w-3.5 h-3.5 text-zinc-600 shrink-0 ml-2" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TeacherStudentList = () => {
  // Data States
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [assignedClassesList, setAssignedClassesList] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [error, setError] = useState(null);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalStudents, setTotalStudents] = useState(0);

  // Layout & UI States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [user, setUser] = useState({
    first_name: "Teacher",
    last_name: "User",
    email: "teacher@school.edu",
  });
  const [notifOpen, setNotifOpen] = useState(false);

  // Academic Period State
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [loadingPeriod, setLoadingPeriod] = useState(true);

  const notificationDropdownRef = useRef(null);

  // 1. Fetch Session Info, Terms, and Teacher's Subjects
  useEffect(() => {
    const storedPerms = sessionStorage.getItem("permissions");
    const storedUser = sessionStorage.getItem("user");

    if (storedPerms) {
      try {
        setPermissions(JSON.parse(storedPerms));
      } catch (err) {
        console.error("Failed to parse permissions:", err);
      }
    }

    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        setUser({
          first_name: userObj.first_name || "Teacher",
          last_name: userObj.last_name || "User",
          username: userObj.username || "teacher",
          email: userObj.email || "teacher@school.edu",
        });
      } catch (err) {
        console.error("Failed to parse user session:", err);
      }
    }

    const fetchCurrentPeriod = async () => {
      try {
        setLoadingPeriod(true);
        const res = await api.get("year-terms/get-set-year-term/");
        setCurrentPeriod(res.data);
      } catch (err) {
        console.error("Failed to fetch active school year-term:", err);
      } finally {
        setLoadingPeriod(false);
      }
    };

    const fetchTeacherSubjects = async () => {
      try {
        const res = await api.get("teacher/subjects/");
        
        // 1. Safely retrieve the array, checking for pagination vs direct array
        const data = res.data.results ? res.data.results : res.data;
        const classes = Array.isArray(data) ? data : [];
        
        setAssignedClassesList(classes);

        // 2. Extract and deduplicate subjects safely
        const uniqueSubjectsMap = new Map();

        classes.forEach((item) => {
          // Extract subject ID and Name based on multiple potential backend formats
          const subjId = 
            item.subject?.id || 
            item.subject_id || 
            item.id;

          const subjName = 
            item.display_name;

          if (subjId && subjName && !uniqueSubjectsMap.has(subjId)) {
            uniqueSubjectsMap.set(subjId, { 
              value: String(subjId), 
              label: subjName 
            });
          }
        });

        // 3. Convert Map values to array and sort alphabetically
        const sortedSubjects = Array.from(uniqueSubjectsMap.values()).sort((a, b) =>
          a.label.localeCompare(b.label)
        );

        setSubjectOptions(sortedSubjects);
      } catch (err) {
        console.error("Failed to load teacher subjects:", err);
      }
    };

    fetchCurrentPeriod();
    fetchTeacherSubjects();
  }, []);

  // Reset to first page when filter parameters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedGender, selectedSubject, pageSize]);

  // 2. Fetch Paginated Students List
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          page: currentPage,
          page_size: pageSize,
        };
        if (searchQuery) params.search = searchQuery;
        if (selectedGender) params.gender = selectedGender;
        if (selectedSubject) params.subject_id = selectedSubject;

        const response = await api.get("teacher-students/", { params });

        if (response.data && response.data.results) {
          setStudents(response.data.results);
          setTotalStudents(response.data.count || response.data.results.length);
        } else if (Array.isArray(response.data)) {
          setStudents(response.data);
          setTotalStudents(response.data.length);
        } else {
          setStudents([]);
          setTotalStudents(0);
        }
      } catch (err) {
        console.error("Failed to load students:", err);
        setError("Could not load student list.");
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchStudents();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedGender, selectedSubject, currentPage, pageSize]);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  const totalPages = Math.ceil(totalStudents / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;

  const genderOptions = [
    { value: "", label: "All Genders" },
    { value: "M", label: "Male" },
    { value: "F", label: "Female" },
  ];

  const subjectSelectOptions = [
    { value: "", label: "All Subjects" },
    ...subjectOptions,
  ];

  const pageSizeOptions = [
    { value: 5, label: "5 per page" },
    { value: 10, label: "10 per page" },
    { value: 20, label: "20 per page" },
    { value: 50, label: "50 per page" },
  ];

  const yearDisplay = currentPeriod?.school_year?.name || currentPeriod?.school_year_detail?.name || "";
  const termDisplay = currentPeriod?.term?.name || currentPeriod?.term_detail?.name || "";

  return (
    <div className="flex h-screen w-full bg-zinc-100 text-zinc-800 font-dynamic-title antialiased overflow-hidden select-none">
      <Sidebar
        permissions={permissions}
        user={user}
        teacherSubjects={assignedClassesList}
        handleLogout={handleLogout}
        onOpenChangePassword={() => setIsModalOpen(true)}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Header
          loading={loadingPeriod}
          loadingPeriod={loadingPeriod}
          yearDisplay={yearDisplay}
          termDisplay={termDisplay}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          notifOpen={notifOpen}
          setNotifOpen={setNotifOpen}
          notificationDropdownRef={notificationDropdownRef}
        />

        <main className="flex-1 overflow-y-auto p-6 custom-scroll">
          <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h1 className="text-sm font-bold text-zinc-800">My Students</h1>
                <p className="text-xs text-zinc-500">
                  List of all students enrolled in your assigned subjects.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                <CustomSelect
                  value={selectedGender}
                  onChange={(val) => setSelectedGender(val)}
                  options={genderOptions}
                  placeholder="All Genders"
                  className="w-36"
                />

                {/*
                <CustomSelect
                  value={selectedSubject}
                  onChange={(val) => setSelectedSubject(val)}
                  options={subjectSelectOptions}
                  placeholder="All Subjects"
                  className="w-64 sm:w-72"
                />*/}

                <div className="relative w-full sm:w-64">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="text"
                    placeholder="Search by name or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-white border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-zinc-400"
                  />
                </div>
              </div>
            </div>

            {/* Table Content */}
            <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm flex flex-col">
              {loading ? (
                <div className="flex items-center justify-center p-12 text-zinc-400 gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-xs">Loading students...</span>
                </div>
              ) : error ? (
                <div className="p-8 text-center text-xs text-rose-500">{error}</div>
              ) : students.length === 0 ? (
                <div className="p-12 text-center text-xs text-zinc-400">
                  No students found matching your parameters.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-zinc-50 border-b border-zinc-200 text-zinc-500 uppercase font-semibold">
                          <th className="py-3 px-4">Student ID</th>
                          <th className="py-3 px-4">Name</th>
                          <th className="py-3 px-4">Gender</th>
                          <th className="py-3 px-4">Subject</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {students.map((student) => (
                          <tr
                            key={student.id}
                            onClick={() => navigate(`/teacher/student/${student.id}`)}
                            className="hover:bg-zinc-50 transition-colors cursor-pointer group"
                          >
                            <td className="py-3 px-4 font-mono text-zinc-500">
                              {student.student_id_number || "-"}
                            </td>
                            <td className="py-3 px-4 font-semibold text-zinc-800">
                              {student.last_name}, {student.first_name} 
                            </td>
                            <td className="py-3 px-4 capitalize text-zinc-600">
                              {student.gender || "-"}
                            </td>
                            <td className="py-3 px-4 text-zinc-600">
                              {student.subjects && student.subjects.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {student.subjects.map((subjName, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 font-medium text-[11px]"
                                    >
                                      {subjName}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Footer */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-zinc-50 border-t border-zinc-200 text-xs text-zinc-500">
                    <div className="flex items-center gap-2">
                      <span>
                        Showing {totalStudents > 0 ? startIndex + 1 : 0} to{" "}
                        {Math.min(startIndex + pageSize, totalStudents)} of {totalStudents} results
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <CustomSelect
                        value={pageSize}
                        onChange={(val) => setPageSize(Number(val))}
                        options={pageSizeOptions}
                        placeholder="Page Size"
                        className="w-32"
                        dropUp={true}
                      />

                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                          className="p-1.5 rounded-lg border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="px-2 font-medium text-zinc-700">
                          {currentPage} / {totalPages}
                        </span>
                        <button
                          type="button"
                          disabled={currentPage >= totalPages}
                          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                          className="p-1.5 rounded-lg border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TeacherStudentList;